import { useState, useCallback, useEffect } from 'react';
import {
  getNotificationsEnabled,
  getNotificationTime,
  checkPermissions,
  enableNotifications as enableNotifs,
  disableNotifications as disableNotifs,
  updateReminderTime as updateTime,
} from '../services/notifications';
import { enablePushNotifications } from '../services/pushNotifications';
import { put } from '../utils/api';
import { useOverallStreak } from './useOverallStreak';
import { isNativeApp } from '../utils/platform';

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

async function syncPrefsToBackend(payload: { enabled: boolean; time: string; timezone: string }): Promise<void> {
  try {
    await put('/api/user/notification-prefs', payload);
  } catch (err) {
    // Non-fatal: localStorage is still source of truth for local reminders.
    console.warn('[Notifications] Failed to sync prefs to backend', err);
  }
}

export function useNotifications() {
  const [enabled, setEnabled] = useState(() => getNotificationsEnabled());
  const [reminderTime, setReminderTime] = useState(() => getNotificationTime());
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const { currentStreak } = useOverallStreak();

  useEffect(() => {
    if (!isNativeApp()) return;
    checkPermissions().then(setPermissionStatus);
  }, []);

  const enableNotifications = useCallback(async () => {
    const granted = await enableNotifs(currentStreak);
    if (granted) {
      setEnabled(true);
      setPermissionStatus('granted');
      // Also enable remote push so the server can deliver reminders.
      enablePushNotifications().catch(() => {});
      syncPrefsToBackend({
        enabled: true,
        time: getNotificationTime(),
        timezone: getBrowserTimezone(),
      });
    }
    return granted;
  }, [currentStreak]);

  const disableNotifications = useCallback(async () => {
    await disableNotifs();
    setEnabled(false);
    syncPrefsToBackend({
      enabled: false,
      time: getNotificationTime(),
      timezone: getBrowserTimezone(),
    });
  }, []);

  const updateReminderTime = useCallback(async (time: string) => {
    setReminderTime(time);
    await updateTime(time, currentStreak);
    syncPrefsToBackend({
      enabled: getNotificationsEnabled(),
      time,
      timezone: getBrowserTimezone(),
    });
  }, [currentStreak]);

  return {
    enabled,
    reminderTime,
    permissionStatus,
    enableNotifications,
    disableNotifications,
    updateReminderTime,
  };
}
